<template>
  <v-dialog v-model="dialog" max-width="700" persistent>
    <template #activator="{ on, attrs }">
      <v-tooltip bottom>
        <template #activator="{ on: tooltipOn, attrs: tooltipAttrs }">
          <button
            class="action-pill pill-dark"
            v-bind="{ ...attrs, ...tooltipAttrs }"
            v-on="{ ...on, ...tooltipOn }"
          >
            <v-icon small>mdi-cog-outline</v-icon>
            <span>Configuración</span>
          </button>
        </template>
        <span>Opciones de configuración del proyecto</span>
      </v-tooltip>
    </template>

    <v-card class="project-settings-modal" elevation="0">

      <!-- Header -->
      <div class="modal-header">
        <div class="header-left">
          <div class="header-icon-wrap">
            <v-icon color="white" size="20">mdi-cog-outline</v-icon>
          </div>
          <div>
            <div class="modal-title">Configuración del Proyecto</div>
            <div class="modal-subtitle">Proyecto: <b>{{ project.name }}</b></div>
          </div>
        </div>
        <v-btn icon small @click="close" class="close-btn">
          <v-icon color="rgba(255,255,255,0.7)" size="18">mdi-close</v-icon>
        </v-btn>
      </div>

      <!-- Tabs -->
      <div class="type-tabs-wrapper">
        <div class="type-tabs">
          <button
            v-for="tab in visibleTabs"
            :key="tab.value"
            class="type-tab"
            :class="{ active: activeTab === tab.value }"
            @click="activeTab = tab.value"
          >
            <v-icon size="14" class="tab-icon">{{ tab.icon }}</v-icon>
            {{ tab.label }}
          </button>
        </div>
      </div>

      <!-- Body -->
      <div class="modal-body">

        <!-- Sub-tabs de cierre (solo cuando está en Cierre) -->
        <template v-if="activeTab === 'closure'">
          <div class="sub-tabs">
            <button
              v-for="type in itemTypes"
              :key="type.value"
              class="sub-tab"
              :class="{ active: selectedClosureType === type.value }"
              @click="selectedClosureType = type.value"
            >
              <v-icon size="13" class="mr-1">{{ type.icon }}</v-icon>
              {{ type.label }}
            </button>
          </div>
        </template>

        <GeneralSection
          v-show="activeTab === 'general'"
          ref="general"
          :project="project"
        />

        <ClosureConfigSection
          v-if="hasClosurePermission"
          v-show="activeTab === 'closure'"
          ref="closure"
          :project="project"
          :selected-type="selectedClosureType"
        />

      </div>

      <!-- Footer -->
      <div class="modal-footer">
        <button class="btn-cancel" @click="close">Cancelar</button>
        <button
          class="btn-save"
          :disabled="!currentSectionValid"
          @click="save"
        >
          <v-icon size="15" color="white" class="mr-1">mdi-content-save-outline</v-icon>
          {{ activeTab === 'general' ? 'Guardar Configuración General' : 'Guardar Sistema de Cierre' }}
        </button>
      </div>

    </v-card>
  </v-dialog>
</template>

<script>
import { checkCompanyLevelPermissions } from '@/helpers/permissions'
import GeneralSection       from './Sections/GeneralSection.vue'
import ClosureConfigSection from './Sections/ClosureConfigSection.vue'

export default {
  name: 'ProjectSettingsModal',

  components: { GeneralSection, ClosureConfigSection },

  props: {
    project: { type: Object, required: true }
  },

  data() {
    return {
      dialog:              false,
      activeTab:           'general',
      selectedClosureType: 'rdi',

      itemTypes: [
        { value: 'rdi',      label: 'RDI',                          icon: 'mdi-file-document-outline' },
        { value: 'tarea',    label: 'Tarea',                        icon: 'mdi-check-circle-outline' },
        { value: 'adicional',label: 'Adicional',                    icon: 'mdi-plus-circle-outline' },
        { value: 'agps_approval', label: 'Aprobación EP [Adicionales]', icon: 'mdi-currency-usd' },
        { value: 'agps',     label: 'Modificar EP [Adicionales]',icon: 'mdi-currency-usd' }
      ]
    }
  },

  computed: {
    hasClosurePermission() {
      return checkCompanyLevelPermissions(['config_closing_system']) || this.hasProjectPermission
    },

		hasProjectPermission(){
			return this.project.user_project?.capabilities?.config_project_closing_system
		},

    visibleTabs() {
      const tabs = [
        { value: 'general', label: 'General', icon: 'mdi-cog-outline' }
      ]
      if (this.hasClosurePermission) {
        tabs.push({ value: 'closure', label: 'Sistema de Cierre', icon: 'mdi-lock-outline' })
      }
      return tabs
    },

    currentSectionValid() {
      if (this.activeTab === 'general') {
        return this.$refs.general ? this.$refs.general.isValid : true
      }
      if (this.activeTab === 'closure') {
        return this.$refs.closure ? this.$refs.closure.isValid : true
      }
      return true
    }
  },

  watch: {
    dialog(val) {
      if (val) {
        this.$nextTick(() => {
          this.$refs.general?.loadConfiguration()
          this.$refs.closure?.loadConfiguration()
        })
      }
    }
  },

  methods: {
    async save() {
      if (this.activeTab === 'general') {
        await this.$refs.general.save()
      } else if (this.activeTab === 'closure') {
        await this.$refs.closure.save()
      }
      this.$store.projectsStore.dispatch('getSelectedProject')
      this.close()
    },

    close() {
      this.dialog              = false
      this.activeTab           = 'general'
      this.selectedClosureType = 'rdi'
    }
  }
}
</script>

<style scoped>
* { box-sizing: border-box; }

.project-settings-modal {
  border-radius: 16px !important;
  overflow: hidden;
  background: #ffffff;
  display: flex !important;
  flex-direction: column !important;
  max-height: 90vh;
}

/* Header */
.modal-header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 18px 22px 16px;
  background: linear-gradient(135deg, #2D4B95 0%, #1a2f63 100%);
  flex-shrink: 0;
}
.header-left { display: flex; align-items: center; gap: 12px; }
.header-icon-wrap {
  width: 36px; height: 36px; border-radius: 10px;
  background: rgba(255,255,255,0.15);
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}
.modal-title    { font-size: 14px; font-weight: 600; color: #fff; line-height: 1.2; }
.modal-subtitle { font-size: 11px; color: rgba(255,255,255,0.6); margin-top: 2px; }
.close-btn { background: rgba(255,255,255,0.1) !important; border-radius: 8px !important; }

/* Tabs */
.type-tabs-wrapper {
  padding: 12px 22px 0;
  background: #f7f9fc;
  border-bottom: 1px solid #e8edf5;
  flex-shrink: 0;
}
.type-tabs { display: flex; gap: 4px; }
.type-tab {
  display: flex; align-items: center; gap: 5px;
  padding: 7px 14px; border-radius: 8px 8px 0 0;
  border: none; background: transparent;
  font-size: 12.5px; font-weight: 500; color: #7889a8;
  cursor: pointer; transition: all 0.18s;
  border-bottom: 2px solid transparent; margin-bottom: -1px;
}
.type-tab:hover { color: #2D4B95; background: rgba(45,75,149,0.05); }
.type-tab.active { color: #2D4B95; font-weight: 600; background: #ffffff; border-bottom-color: #2D4B95; }
.tab-icon { opacity: 0.7; }

/* Body */
.modal-body {
  padding: 20px 22px;
  overflow-y: auto;
  flex: 1 1 auto;
}

/* Sub-tabs (tipos de cierre) */
.sub-tabs {
  display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 18px;
}
.sub-tab {
  display: inline-flex; align-items: center;
  padding: 6px 12px; border-radius: 20px;
  border: 1.5px solid #e2e8f4; background: transparent;
  font-size: 12px; font-weight: 500; color: #7889a8;
  cursor: pointer; transition: all 0.15s;
}
.sub-tab:hover { border-color: #2D4B95; color: #2D4B95; background: #eef2fb; }
.sub-tab.active { border-color: #2D4B95; background: #eef2fb; color: #2D4B95; font-weight: 600; }

/* Footer */
.modal-footer {
  display: flex; align-items: center; justify-content: flex-end;
  gap: 8px; padding: 13px 22px;
  border-top: 1px solid #e8edf5; background: #f7f9fc;
  flex-shrink: 0;
}
.btn-cancel {
  padding: 8px 16px; border-radius: 8px;
  border: 1.5px solid #d1d9e8; background: transparent;
  font-size: 13px; font-weight: 500; color: #5a6a8a; cursor: pointer;
}
.btn-cancel:hover { background: #eef2fb; border-color: #b0bedd; }
.btn-save {
  display: flex; align-items: center;
  padding: 8px 18px; border-radius: 8px; border: none;
  background: linear-gradient(135deg, #2D4B95, #1a2f63);
  font-size: 13px; font-weight: 600; color: white;
  cursor: pointer; box-shadow: 0 3px 10px rgba(45,75,149,0.3);
}
.btn-save:hover:not(:disabled) { box-shadow: 0 5px 14px rgba(45,75,149,0.4); transform: translateY(-1px); }
.btn-save:disabled { opacity: 0.45; cursor: not-allowed; box-shadow: none; }

/* Activator */
.pill-dark {
  display: inline-flex; align-items: center; gap: 4px;
  padding: 5px 11px 5px 8px; border-radius: 99px;
  border: none; cursor: pointer; font-size: 11px; font-weight: 600;
  background: rgba(45,75,149,0.12); color: #2D4B95;
  transition: filter 0.15s, transform 0.15s; outline: none;
}
.pill-dark:hover { filter: brightness(1.08); transform: translateY(-1px); }
.pill-dark .v-icon { font-size: 14px !important; color: #2D4B95; }
</style>
